import { customColors } from '@apollo/orbit';
import classnames from 'classnames';
import { GraphQLField, GraphQLInputType, GraphQLOutputType } from 'graphql';
import React from 'react';

import { getSubjectSlugByGraphQLType } from 'src/app/graph/documentationPage/schemaPage/getSubjectSlugByGraphQLType';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { TypeString } from 'src/components/typeString/TypeString';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { CoordinateKind } from 'src/lib/graphqlTypes/types';

import { fieldRouteConfig, referenceRouteConfig } from '../../../../../routes';

const declarationKeywordByCoordinateKind = new Map<CoordinateKind, string>([
  [CoordinateKind.ENUM_VALUE, 'enum'],
  [CoordinateKind.INPUT_OBJECT_FIELD, 'input'],
  [CoordinateKind.OBJECT_FIELD, 'type'],
]);

const fieldTypeName = (rawFieldName?: GraphQLOutputType | GraphQLInputType) =>
  rawFieldName?.toString()?.replace(/[\[\]!]/g, '');

interface FieldPageCoordinateSignatureProps {
  field: GraphQLField<unknown, unknown>;
  isRootType: boolean;
  coordinateKind: CoordinateKind;
}

export const FieldPageCoordinateSignature = ({
  field,
  isRootType,
  coordinateKind,
}: FieldPageCoordinateSignatureProps) => {
  const { graphId, graphVariant, typeName } = useRouteParams(fieldRouteConfig);

  const fieldLocation = referenceRouteConfig.location({
    graphId,
    graphVariant,
    category: getSubjectSlugByGraphQLType(field.type),
    graphqlTypeName: fieldTypeName(field.type),
  });

  return (
    <div>
      <div className="font-mono" style={{ color: customColors.code.a }}>
        {isRootType
          ? 'root'
          : declarationKeywordByCoordinateKind.get(coordinateKind)}{' '}
        {typeName} {' {'}
      </div>
      <div className="ml-3">
        <Tooltip
          disabled={!field.deprecationReason}
          label={field.deprecationReason}
          placement="top-start"
        >
          <span
            className={classnames('font-mono text-primary', {
              'line-through': field.isDeprecated,
            })}
          >
            {field.name}
          </span>
        </Tooltip>
        {!field.args?.length ? (
          <>
            :{' '}
            <TypeString
              printedType={field.type.toString()}
              to={fieldLocation}
            />
          </>
        ) : (
          <>
            {' ('}
            <>
              {field.args.map((arg) => (
                <div key={arg.name}>
                  <span className="ml-4 font-mono text-primary">
                    {arg.name}:{' '}
                  </span>
                  <TypeString
                    printedType={arg.type.toString()}
                    to={referenceRouteConfig.location({
                      graphId,
                      graphVariant,
                      category: getSubjectSlugByGraphQLType(arg.type),
                      graphqlTypeName: fieldTypeName(arg.type),
                    })}
                  />
                </div>
              ))}
            </>
            {'): '}
            <TypeString
              printedType={field.type.toString()}
              to={fieldLocation}
            />
          </>
        )}
      </div>
      <div style={{ color: customColors.code.a }}>{' }'}</div>
    </div>
  );
};
